import { get } from 'lodash';
import { AccountType, AnyObject, RecordType } from '@xbcb/shared-types';
import { ImporterEntityActivation } from '../../../loadableComponents';
import { WorkOrderRoute } from '..';
import {
  getImporterEntityVersion,
  getUsIorVersion,
  queryImporterEntityActivation,
  updateImporterEntityActivation,
  upsertImporterEntityActivationMutation,
} from './importerEntityActivation.query';
import {
  WorkOrderMilestoneName,
  WorkOrderStatus,
  WorkOrderTaskType,
} from '@xbcb/work-order-types';
import { isImporterEntityStatusNonMutable } from '@xbcb/party-utils';
import { showValidationErrors } from '@xbcb/ui-utils';
import { v4 } from 'uuid';
import { ActivationStatus, DpsStatusCode } from '@xbcb/party-types';
import { UsIor } from '@xbcb/api-gateway-client';
import { setModal } from 'actions';
import { message } from 'antd';
import { executeMutation } from 'libs/executeMutation';
import { createWorkOrderMilestoneMutation } from 'libs/sharedQueries';
import { AppRecordOnConfirmPayload } from 'routes/types';
import { ModalKey } from 'types';
import { importerEntityActivationKebabMenuItems } from './kebabMenuItems';
import { updateUsIorActivationStatus } from '../shared/usIorActivationUtils';
import { completeWorkOrderTaskButton } from '../../utils/completeWorkOrderTaskButton';
import {
  ImporterEntity,
  WorkOrderTask,
  UsIorStatus,
  ImporterEntityStatus,
  WorkOrderTaskStatus,
} from '../../../../__generated__/graphql';
import { validateAddress } from '../../../../libs/validateAddress';
import { client as apolloClient } from '@xbcb/apollo-client';
import { gql } from '@apollo/client';
import log from '@xbcb/log';

const isDpsCleared = (record: AnyObject): boolean => {
  const entity = record?.importerEntity;
  const usIor = record?.usIor;

  // For US entities, check usIor DPS status
  if (usIor) {
    const dpsStatus = usIor.dpsRecord?.overallDpsStatus;
    return dpsStatus === DpsStatusCode.CLEARED;
  }

  // For non-US entities, check entity DPS status
  if (entity) {
    const dpsStatus = entity.dpsRecord?.overallDpsStatus;
    return dpsStatus === DpsStatusCode.CLEARED;
  }

  return false;
};

const importerEntityTopLevelStatusIsImmutable = (
  entity: ImporterEntity | UsIor,
): boolean => {
  const status = entity?.status;
  if (!status) return false;
  return [
    ImporterEntityStatus.APPROVED,
    ImporterEntityStatus.REJECTED,

    UsIorStatus.APPROVED,
    UsIorStatus.REJECTED,
  ].includes(status as UsIorStatus | ImporterEntityStatus);
};

const showActivationButtons = (
  existingRecord: AnyObject,
  currentUser: AnyObject,
  options: { taskType?: WorkOrderTaskType } = {},
) => {
  const isOperator = currentUser.accountType === AccountType.OPERATOR;
  const invalidWorkOrderStatus = [
    WorkOrderStatus.COMPLETED,
    WorkOrderStatus.BLOCKED,
    WorkOrderStatus.CANCELED,
  ].includes(existingRecord?.status);
  const taskCreated = Boolean(existingRecord?.tasks.length);

  const entity = existingRecord?.usIor ?? existingRecord?.importerEntity;
  const invalidImporterEntityStatus = isImporterEntityStatusNonMutable({
    iorEntity: entity,
    customsBrokerId: existingRecord?.broker?.id,
  });
  const isApprovedOrRejected = importerEntityTopLevelStatusIsImmutable(entity);

  const taskIsAssigned = existingRecord?.tasks?.some((task: WorkOrderTask) => {
    const statusMatch = [
      WorkOrderTaskStatus.ASSIGNED,
      WorkOrderTaskStatus.IN_PROGRESS,
    ].includes(task.status);
    const taskTypeMatch = options.taskType
      ? (task.definition?.workOrderTaskType as unknown) === options.taskType
      : true;
    return statusMatch && taskTypeMatch;
  });

  return (
    isOperator &&
    !invalidImporterEntityStatus &&
    !existingRecord.deleted?.time &&
    !invalidWorkOrderStatus &&
    taskCreated &&
    !isApprovedOrRejected &&
    taskIsAssigned
  );
};

const isUsIorIEA = (existingRecord: AnyObject) => {
  const usIor = existingRecord?.usIor as UsIor | undefined;
  return Boolean(usIor);
};

const showRejectButton = (
  existingRecord: AnyObject,
  currentUser: AnyObject,
) => {
  const isOperator = currentUser.accountType === AccountType.OPERATOR;
  const isNotDeleted = !existingRecord.deleted?.time;
  const isValidWorkOrderStatus = ![
    WorkOrderStatus.CANCELED,
    WorkOrderStatus.READY,
  ].includes(existingRecord?.status);
  const hasAssignedTask = existingRecord?.tasks?.some((task: WorkOrderTask) =>
    [
      WorkOrderTaskStatus.ASSIGNED,
      WorkOrderTaskStatus.IN_PROGRESS,
      WorkOrderTaskStatus.COMPLETED,
    ].includes(task.status),
  );

  return (
    isOperator && isNotDeleted && isValidWorkOrderStatus && hasAssignedTask
  );
};

const operatorHasTakenAction = (existingRecord: AnyObject) => {
  const usIor = existingRecord?.usIor as UsIor | undefined;
  const operatorHasTakenAction =
    Boolean(usIor) &&
    usIor?.activations?.some(
      ({ status }) =>
        status &&
        [ActivationStatus.PENDING_ACTIVATION].includes(
          status as ActivationStatus,
        ),
    );

  return operatorHasTakenAction;
};

const brokerPath = 'broker.id';
const pathToUsIor = ['usIor'];

const completeTaskButton = completeWorkOrderTaskButton({
  skipUpdateRecordForWorkOrderTaskTypes: [
    WorkOrderTaskType.IMPORTER_REVIEW,
    WorkOrderTaskType.IMPORTER_ACTIVATION,
  ],
});

const validateDocumentsApproved = async (
  updatedRecord: AnyObject,
): Promise<boolean> => {
  const importerEntity = updatedRecord?.importerEntity;
  const usIor = updatedRecord?.usIor;

  // Check if all documents are approved (entity, importer, and usIor level)
  const entityReadinessId = importerEntity?.program?.[0]?.docReadiness?.id;
  const importerReadinessId =
    importerEntity?.importer?.docReadinesses?.[0]?.id ||
    usIor?.importer?.docReadinesses?.[0]?.id;
  const usIorReadinessId = usIor?.docReadinessId;

  const readinessIds = [
    entityReadinessId,
    importerReadinessId,
    usIorReadinessId,
  ].filter(Boolean);

  if (readinessIds.length > 0) {
    try {
      const INVOKE_ANDRA_API = gql`
        mutation InvokeAndraApi($input: AndraClientConnectionInput!) {
          invokeAndraApi(input: $input) {
            response
            error
          }
        }
      `;

      // Fetch all readiness documents in parallel
      const responses = await Promise.all(
        readinessIds.map((readinessId) =>
          apolloClient.mutate({
            mutation: INVOKE_ANDRA_API,
            variables: {
              input: {
                apiName: 'GetDocsReadiness',
                apiArgs: {
                  clientIdentifier: 'CBMS',
                  readinessId,
                  programType: 'AMAZON_FBA_INBOUND',
                  docRegion: 'USAmazon',
                },
                sessionName: 'importerEntityActivation',
              },
            },
          }),
        ),
      );

      // Collect all uploaded documents
      const allUploadedDocs = responses.flatMap(
        (response) =>
          response.data?.invokeAndraApi?.response?.readiness?.uploadedDocs ||
          [],
      );

      // Check if all documents are approved (excluding BOND documents)
      const hasUnapprovedDoc = allUploadedDocs.some(
        (doc: any) =>
          doc.basicInfo?.docType !== 'BOND' &&
          doc.basicInfo?.docStatus !== 'APPROVED' &&
          doc.basicInfo?.docStatus !== 'AUTO_APPROVED' &&
          doc.basicInfo?.docStatus !== 'COMPLETE',
      );

      if (hasUnapprovedDoc) {
        void message.error(
          'All documents must be approved before activating the importer entity',
        );
        return false;
      }
    } catch (error) {
      void message.error('Failed to verify document status');
      return false;
    }
  }

  return true;
};

const validateActivationFields = (usIor: UsIor) => {
  const { addresses, industry, businessStructure } = usIor;

  const additionalErrors = [
    ...validateAddress({
      addressType: 'mailing',
      addressObject: addresses?.mailing,
    }),
    ...validateAddress({
      addressType: 'physical',
      addressObject: addresses?.physical,
    }),
  ];

  if (!businessStructure) {
    additionalErrors.push({
      title: 'Missing Business Structure',
      messages: ['Importer must have a business structure'],
    });
  }

  return additionalErrors;
};

const importerEntityActivationRoute: WorkOrderRoute = {
  Page: ImporterEntityActivation,
  recordType: RecordType.IMPORTER_ENTITY_ACTIVATION,
  queries: {
    get: queryImporterEntityActivation,
    update: updateImporterEntityActivation,
  },
  disableCancel: true,
  kebabMenuItems: importerEntityActivationKebabMenuItems,
  getAdditionalWorkOrderInfo: ({ record }) => {
    const usIor = record?.usIor as UsIor;
    if (!usIor) return [];

    return [
      {
        label: 'CBP Status',
        value: usIor.cbpStatus,
      },
      {
        label: 'CBP Rejection Reasons',
        value: usIor.cbp5106RejectionReasons
          ?.map((r) => `${r.code}: ${r.name}`)
          .join(', '),
      },
      {
        label: 'CBP Number',
        value: usIor.iorNumber?.value,
      },
    ].filter(({ value }) => Boolean(value));
  },
  submitButtons: [
    {
      key: `activateImporterEntity`,
      text: 'Approve',
      skipUpdateRecord: () => true,
      skipValidation: () => true,
      show: ({ existingRecord, currentUser }) => {
        return (
          showActivationButtons(existingRecord, currentUser) &&
          !isUsIorIEA(existingRecord)
        );
      },
      onSubmit: async ({ updatedRecord, history }) => {
        // Check DPS status first
        if (!isDpsCleared(updatedRecord)) {
          void message.error(
            'DPS status must be CLEARED before activating the importer entity',
          );
          return;
        }

        // Check if all documents are approved
        const documentsApproved = await validateDocumentsApproved(
          updatedRecord,
        );
        if (!documentsApproved) {
          return;
        }

        const importerEntity = updatedRecord?.importerEntity;
        try {
          const version = await getImporterEntityVersion(importerEntity.id);
          const { errors } = await apolloClient.mutate({
            mutation: upsertImporterEntityActivationMutation(),
            variables: {
              id: importerEntity?.id,
              version: version || importerEntity.version,
              input: {
                customsBroker: { id: updatedRecord?.broker?.id },
                status: ActivationStatus.ACTIVATED,
              },
            },
            errorPolicy: 'all',
          });

          if (errors) {
            void message.error(
              'Failed to activate importer entity. Please try again.',
            );
            return;
          }

          // Only create milestone if no errors
          void message.success('Importer Entity Activated');
          await executeMutation({
            mutation: createWorkOrderMilestoneMutation,
            successMessage: 'Milestone Created',
            variables: {
              idempotencyKey: v4(),
              input: {
                allowDuplicate: false,
                name: WorkOrderMilestoneName.IOR_ACTIVATED,
                workOrder: {
                  id: updatedRecord.id,
                  version: updatedRecord.version,
                },
              },
            },
          });
          history.push('/assignment-workspace');
        } catch (error) {
          log.error('Importer entity activation failed:', error);
          void message.error(
            'Failed to activate importer entity. Please try again.',
          );
          return;
        }
      },
    },
    {
      key: `rejectImporterEntity`,
      text: 'Reject',
      skipUpdateRecord: () => true,
      skipValidation: () => true,
      show: ({ existingRecord, currentUser }) => {
        return (
          showRejectButton(existingRecord, currentUser) &&
          !isUsIorIEA(existingRecord)
        );
      },
      onConfirm: ({ dispatch, isValid }) =>
        new Promise<AppRecordOnConfirmPayload>((resolve, reject) => {
          if (isValid) {
            dispatch(
              setModal({
                key: ModalKey.REJECT_IOR_ACTIVATION,
                props: {
                  visible: true,
                  sendConfirmationDecision: resolve,
                },
              }),
            );
          } else {
            return resolve({ canceled: true });
          }
        }),
      onSubmit: async ({ updatedRecord, confirmationData, history }) => {
        const importerEntity = updatedRecord?.importerEntity;
        const { cancellationReason } = confirmationData || {};
        if (!cancellationReason) {
          void message.error(`Please fill out all the fields`);
          return;
        }
        const version = await getImporterEntityVersion(importerEntity.id);

        await executeMutation({
          mutation: upsertImporterEntityActivationMutation(),
          successMessage: 'Importer Entity Rejected',
          variables: {
            id: importerEntity?.id,
            version: version || importerEntity.version,
            input: {
              customsBroker: { id: updatedRecord?.broker?.id },
              status: ActivationStatus.REJECTED,
              rejectionReasons: cancellationReason,
            },
          },
        });
        await executeMutation({
          mutation: createWorkOrderMilestoneMutation,
          successMessage: 'Milestone Created',
          variables: {
            idempotencyKey: v4(),
            input: {
              allowDuplicate: false,
              name: WorkOrderMilestoneName.IOR_REJECTED,
              workOrder: {
                id: updatedRecord.id,
                version: updatedRecord.version,
              },
            },
          },
        });
        history.push('/assignment-workspace');
      },
    },
    {
      key: 'activateUsIor',
      text: 'Approve',
      skipUpdateRecord: () => true,
      skipValidation: () => true,
      show: ({ existingRecord, currentUser }) => {
        const showForUs = isUsIorIEA(existingRecord);
        const showButtons = showActivationButtons(existingRecord, currentUser, {
          taskType: WorkOrderTaskType.IMPORTER_ACTIVATION,
        });
        const noRejectionReasons = !get(existingRecord, [
          ...pathToUsIor,
          'cbp5106RejectionReasons',
        ]);
        const noActionTaken = !operatorHasTakenAction(existingRecord);
        return showForUs && showButtons && noRejectionReasons && noActionTaken;
      },
      onSubmit: async ({ updatedRecord, dispatch, history }) => {
        // Check DPS status first
        if (!isDpsCleared(updatedRecord)) {
          void message.error(
            'DPS status must be CLEARED before activating the importer entity',
          );
          return;
        }

        const usIor = get(updatedRecord, pathToUsIor);
        const customsBrokerId = get(updatedRecord, brokerPath.split('.'));

        // Check if all documents are approved
        const documentsApproved = await validateDocumentsApproved(
          updatedRecord,
        );
        if (!documentsApproved) {
          return;
        }

        const additionalErrors = [...validateActivationFields(usIor)];
        if (additionalErrors?.length) {
          showValidationErrors(additionalErrors);
          return;
        } else {
          try {
            const usIorVersion = await getUsIorVersion(usIor?.id);
            const { errors } = await apolloClient.mutate({
              mutation: updateUsIorActivationStatus,
              variables: {
                id: usIor?.id,
                version: usIorVersion || usIor.version,
                usCustomsBrokerId: customsBrokerId,
                activationStatus: ActivationStatus.PENDING_ACTIVATION,
              },
              errorPolicy: 'all',
            });

            if (errors) {
              void message.error(
                'Failed to activate US IOR. Please try again.',
              );
              return;
            }

            // Only create milestone if no errors
            void message.success('IOR Activation status updated');
            await executeMutation({
              mutation: createWorkOrderMilestoneMutation,
              successMessage: 'Milestone Created',
              variables: {
                idempotencyKey: v4(),
                input: {
                  allowDuplicate: false,
                  name: WorkOrderMilestoneName.IOR_ACTIVATED,
                  workOrder: {
                    id: updatedRecord.id,
                    version: updatedRecord.version,
                  },
                },
              },
            });
            history.push('/assignment-workspace');
          } catch (error) {
            log.error('US IOR activation failed:', error);
            void message.error('Failed to activate US IOR. Please try again.');
            return;
          }
        }
      },
    },
    {
      key: `rejectUsIor`,
      text: 'Reject',
      skipUpdateRecord: () => true,
      skipValidation: () => true,
      show: ({ existingRecord, currentUser }) => {
        return (
          showRejectButton(existingRecord, currentUser) &&
          isUsIorIEA(existingRecord)
        );
      },
      onConfirm: ({ dispatch, isValid }) =>
        new Promise<AppRecordOnConfirmPayload>((resolve, reject) => {
          if (isValid) {
            dispatch(
              setModal({
                key: ModalKey.REJECT_IOR_ACTIVATION,
                props: {
                  visible: true,
                  sendConfirmationDecision: resolve,
                },
              }),
            );
          } else {
            return resolve({ canceled: true });
          }
        }),
      onSubmit: async ({ updatedRecord, confirmationData, history }) => {
        const usIor = updatedRecord?.usIor;
        const customsBrokerId = get(updatedRecord, brokerPath.split('.'));

        const { cancellationReason } = confirmationData || {};
        if (!cancellationReason) {
          void message.error(`Please fill out all the fields`);
          return;
        }

        const usIorVersion = await getUsIorVersion(usIor.id);
        // upsert rejection in activations list
        await executeMutation({
          mutation: updateUsIorActivationStatus,
          successMessage: 'IOR Activation status updated',
          variables: {
            id: usIor?.id,
            version: usIorVersion || usIor.version,
            usCustomsBrokerId: customsBrokerId,
            activationStatus: ActivationStatus.REJECTED,
            rejectionReasons: cancellationReason,
          },
        });

        await executeMutation({
          mutation: createWorkOrderMilestoneMutation,
          successMessage: 'Milestone Created',
          variables: {
            idempotencyKey: v4(),
            input: {
              allowDuplicate: false,
              name: WorkOrderMilestoneName.IOR_REJECTED,
              workOrder: {
                id: updatedRecord.id,
                version: updatedRecord.version,
              },
            },
          },
        });
        history.push('/assignment-workspace');
      },
    },
    {
      ...completeTaskButton,
      show: (props) => {
        const baseShow = completeTaskButton.show(props);
        const { workOrderTaskType, existingRecord } = props;

        if (!workOrderTaskType) return false; // if we're not currently viewing a task, don't show complete button

        const currentTask: WorkOrderTask = existingRecord.tasks?.find(
          (task: WorkOrderTask) => {
            const taskType = task?.definition
              ?.workOrderTaskType as unknown as WorkOrderTaskType;
            return taskType === workOrderTaskType;
          },
        );

        // a step is manually completable if its final step has isFinalConfirmation=true
        const taskIsManuallyCompletable = Boolean(
          currentTask.steps?.find((step) => step.isFinalConfirmation),
        );

        return baseShow && taskIsManuallyCompletable;
      },
    },
  ],
};

export default importerEntityActivationRoute;
